/**
 * Decides when EmbeddedCheckoutModal may request a Checkout Session and
 * whether a response may still touch the UI (Oct 2026 hotfix).
 *
 * The bug: the modal's fetch effect depended on parent callback identity, so
 * any parent re-render 7-21 s after the Stripe form had loaded sent a second
 * create request. Its `409 intro_offer_checkout_in_progress` then replaced the
 * loaded form with an error. Rules enforced here:
 *
 * - one automatic request per open (per plan/cycle/trial/promo key), whatever
 *   the parent does;
 * - no concurrent requests;
 * - a mounted Stripe form is never replaced by a non-manual request;
 * - responses from before a close (or a key change) are ignored.
 */
export type CheckoutRequestGate = {
  /** Open effect: true only for the first call per open and request key. */
  shouldRequestOnOpen(key: string): boolean;
  /** Modal closed: forget the key and invalidate any in-flight attempt. */
  close(): void;
  /** Start a request. Returns its attempt id, or null when it must not run. */
  tryBegin(options?: { manualRetry?: boolean }): number | null;
  /** Release the in-flight lock if `attempt` is still current. */
  finish(attempt: number): void;
  isCurrent(attempt: number): boolean;
  /** Record the client secret now mounted in the Stripe form (or null). */
  setMountedSecret(secret: string | null): void;
  hasMountedForm(): boolean;
};

export function createCheckoutRequestGate(): CheckoutRequestGate {
  let requestedKey: string | null = null;
  let attempt = 0;
  let inFlight = false;
  let mountedSecret: string | null = null;

  return {
    shouldRequestOnOpen(key) {
      if (requestedKey === key) return false;
      if (requestedKey !== null) {
        // Selection changed while open: the old session is for another plan.
        attempt += 1;
        inFlight = false;
        mountedSecret = null;
      }
      requestedKey = key;
      return true;
    },
    close() {
      requestedKey = null;
      attempt += 1;
      inFlight = false;
      mountedSecret = null;
    },
    tryBegin(options) {
      if (inFlight) return null;
      if (mountedSecret && !options?.manualRetry) return null;
      if (options?.manualRetry) mountedSecret = null;
      inFlight = true;
      attempt += 1;
      return attempt;
    },
    finish(id) {
      if (id === attempt) inFlight = false;
    },
    isCurrent(id) {
      return id === attempt;
    },
    setMountedSecret(secret) {
      mountedSecret = secret;
    },
    hasMountedForm() {
      return mountedSecret !== null;
    },
  };
}

export function checkoutRequestKey(params: {
  planTier: string;
  billingCycle: string;
  cardTrial: boolean;
  trialPeriodDays: number;
  promo?: string;
}): string {
  return [
    params.planTier,
    params.billingCycle,
    params.cardTrial ? 'trial' : 'paid',
    params.trialPeriodDays,
    params.promo || '',
  ].join(':');
}
